import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Pencil, Plus } from 'lucide-react';
import { Button, Card, DataTable, EmptyState, StatTile, formatDatum, formatEuro, type Column } from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { LoanDialog } from './LoanDialog';
import { loansApi } from '@/lib/api';
import type { Loan, LoanScheduleEntry } from '@/types';

const PLAN_SPALTEN: Column<LoanScheduleEntry>[] = [
  { key: 'period', header: 'Nr.', mono: true, mobil: 'unter', render: (z) => z.period },
  {
    key: 'due_date',
    header: 'Fällig',
    mono: true,
    mobil: 'titel',
    render: (z) => (
      <>
        {formatDatum(z.due_date)}
        {z.is_grace && <span className="ml-1.5 font-sans t-small">tilgungsfrei</span>}
      </>
    ),
  },
  { key: 'payment', header: 'Rate', align: 'end', mono: true, mobil: 'unter', render: (z) => formatEuro(z.payment, { cent: true }) },
  { key: 'interest', header: 'Zins', align: 'end', mono: true, render: (z) => formatEuro(z.interest, { cent: true }) },
  { key: 'principal_part', header: 'Tilgung', align: 'end', mono: true, render: (z) => formatEuro(z.principal_part, { cent: true }) },
  {
    key: 'remaining_balance',
    header: 'Restschuld',
    align: 'end',
    mono: true,
    mobil: 'wert',
    render: (z) => <span className="font-medium text-foreground">{formatEuro(z.remaining_balance)}</span>,
  },
];

export function KreditPage() {
  const queryClient = useQueryClient();

  const [dialogOffen, setDialogOffen] = useState(false);
  const [inBearbeitung, setInBearbeitung] = useState<Loan | null>(null);
  const [geoeffnet, setGeoeffnet] = useState<string | null>(null);

  const kredite = useQuery({ queryKey: ['loans'], queryFn: loansApi.list });

  const plan = useQuery({
    queryKey: ['loan-schedule', geoeffnet],
    queryFn: () => loansApi.schedule(geoeffnet!),
    enabled: Boolean(geoeffnet),
  });

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['loans'] });
    void queryClient.invalidateQueries({ queryKey: ['loan-schedule'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
    void queryClient.invalidateQueries({ queryKey: ['projection'] });
  };

  const oeffneDialog = (kredit: Loan | null) => {
    setInBearbeitung(kredit);
    setDialogOffen(true);
  };

  const liste = kredite.data ?? [];

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Kredit"
        subtitle="Konditionen, Restschuld und Tilgungsplan. Die Rate fließt automatisch in die Liquiditätsplanung ein."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => oeffneDialog(null)}>
            Darlehen anlegen
          </Button>
        }
      />

      {liste.length === 0 && !kredite.isLoading ? (
        <Card>
          <EmptyState
            icon={Landmark}
            title="Noch kein Darlehen"
            description="Darlehen erscheinen hier mit Rate und Restschuld, sobald eins mit seinen Konditionen angelegt ist; beides fließt dann in die Projektion."
          />
        </Card>
      ) : (
        liste.map((kredit) => (
          <Card
            key={kredit.id}
            title={kredit.name}
            description={`Auszahlung ${formatDatum(kredit.disbursement_date)} · erste Rate ${formatDatum(kredit.first_payment_date)}${
              kredit.grace_months > 0 ? ` · ${kredit.grace_months} Monate tilgungsfrei` : ''
            }`}
            actions={
              <>
                <Button size="sm" icon={Pencil} onClick={() => oeffneDialog(kredit)}>
                  Bearbeiten
                </Button>
                <Button size="sm" onClick={() => setGeoeffnet(geoeffnet === kredit.id ? null : kredit.id)}>
                  {geoeffnet === kredit.id ? 'Plan schließen' : 'Tilgungsplan'}
                </Button>
              </>
            }
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile label="Darlehenssumme" value={formatEuro(kredit.principal, { cent: true })} />
              <StatTile label="Restschuld heute" value={formatEuro(kredit.remaining_balance, { cent: true })} />
              <StatTile label="Monatsrate" value={formatEuro(kredit.monthly_payment, { cent: true })} />
              <StatTile
                label="Zins / Laufzeit"
                value={`${Number(kredit.interest_rate).toLocaleString('de-DE')} % · ${kredit.term_months} Mon.`}
              />
            </div>

            {geoeffnet === kredit.id && (
              <div className="mt-5 space-y-3 border-t border-border pt-4">
                {plan.data && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <span className="text-muted-foreground">
                      Zinsen gesamt:{' '}
                      <strong className="font-medium tabular text-foreground">{formatEuro(plan.data.total_interest, { cent: true })}</strong>
                    </span>
                    <span className="text-muted-foreground">
                      Zahlungen gesamt:{' '}
                      <strong className="font-medium tabular text-foreground">{formatEuro(plan.data.total_payment, { cent: true })}</strong>
                    </span>
                  </div>
                )}
                <DataTable
                  columns={PLAN_SPALTEN}
                  rows={plan.data?.entries ?? []}
                  rowKey={(z) => String(z.period)}
                  loading={plan.isLoading}
                  maxHeight={384}
                  label={`Tilgungsplan ${kredit.name}`}
                />
              </div>
            )}
          </Card>
        ))
      )}

      <LoanDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        onGespeichert={aktualisieren}
        kredit={inBearbeitung}
      />
    </div>
  );
}
